/**
 * VersionEditorModal — create a Draft version / edit a Draft's settings.
 *
 * Wire endpoints:
 *   POST  /v3/admin/disclosures/versions               (create — always a Draft)
 *   PATCH /v3/admin/disclosures/versions/{versionId}   (Draft only; server refuses otherwise)
 *
 * 2026-10-07 rewrite:
 *   - "Only when" is a picker over the PresenceCondition vocabulary (the server refuses
 *     unknown tokens). The old free-text box suggested an invented syntax
 *     ("cart.customerType == ...") that the package builder never understood.
 *   - A new draft can start from a copy of an existing version's body (usually the
 *     active one) so a change is an edit, not a retype.
 *   - Effective from / to, variant selector and feature flag are no longer offered:
 *     the package builder does not read them (live = status Active), so editing them
 *     changed nothing. Values already on a row are left untouched (PATCH skips them).
 *   - The author is stamped by the server from the sign-in, not sent from here.
 */

import { useEffect, useMemo, useState } from 'react';

import { useDisclosuresConfig } from '@/contexts/DisclosuresContext';
import {
  createVersion,
  updateVersion,
  type DisclosureTemplateVersionUpsertInput,
} from '@/services/disclosuresAdminApi';
import type { DisclosureVersionRow } from '@/services/disclosuresApi';

import {
  ChipPicker,
  Field,
  FieldGrid,
  Modal,
  NumberInput,
  Select,
  TextArea,
  TextInput,
  useActor,
  type PickItem,
} from './_shared';
import { liveVersion, splitCondition, statusLabel, versionsFor } from './disclosureModel';

export interface VersionEditorModalProps {
  open: boolean;
  templateCode: string;
  initial?: DisclosureVersionRow | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}

interface FormShape {
  versionNumber: string;
  pageCount: string;
  signatureCount: string;
  conditions: string[];
  notes: string;
  copyFromVersionId: string;
}

function nextVersionNumber(existing: ReadonlyArray<DisclosureVersionRow>): string {
  const nums = existing
    .map((v) => Number.parseFloat(String(v.versionNumber ?? '')))
    .filter((n) => Number.isFinite(n));
  if (nums.length === 0) return '1.0';
  const max = Math.max(...nums);
  return (Math.floor(max) + 1).toFixed(1);
}

export function VersionEditorModal({
  open,
  templateCode,
  initial,
  onClose,
  onSaved,
  onError,
}: VersionEditorModalProps): JSX.Element | null {
  const actor = useActor();
  const { envelope, options } = useDisclosuresConfig();
  const editing = initial !== null && initial !== undefined;
  const siblings = useMemo(() => versionsFor(envelope.versions, templateCode), [envelope.versions, templateCode]);

  const blank = (): FormShape => {
    const live = liveVersion(siblings);
    const seed = live ?? siblings[0] ?? null;
    return {
      versionNumber: nextVersionNumber(siblings),
      pageCount: String(seed?.pageCount ?? 1),
      signatureCount: String(seed?.signatureCount ?? 0),
      conditions: splitCondition(seed?.conditionExpression),
      notes: '',
      copyFromVersionId: seed ? String(seed.versionId) : '',
    };
  };
  const fromRow = (row: DisclosureVersionRow): FormShape => ({
    versionNumber: String(row.versionNumber ?? ''),
    pageCount: String(row.pageCount ?? 0),
    signatureCount: String(row.signatureCount ?? 0),
    conditions: splitCondition(row.conditionExpression),
    notes: String(row.notes ?? ''),
    copyFromVersionId: '',
  });

  const [form, setForm] = useState<FormShape>(() => (initial ? fromRow(initial) : blank()));
  const [busy, setBusy] = useState<boolean>(false);
  const [inlineErr, setInlineErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initial ? fromRow(initial) : blank());
      setInlineErr(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = <K extends keyof FormShape>(key: K, value: FormShape[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const conditionItems = useMemo<PickItem[]>(
    () => (options?.presenceConditions ?? []).map((c) => ({ value: c.value, label: c.label, meaning: c.meaning })),
    [options],
  );

  const canSave = !busy && form.versionNumber.trim().length > 0 && options !== null;

  const submit = async (): Promise<void> => {
    if (!canSave) return;
    setBusy(true);
    setInlineErr(null);
    try {
      const num = (s: string): number | null => {
        if (!s.trim()) return null;
        const n = Number(s);
        return Number.isFinite(n) ? n : null;
      };
      const payload: DisclosureTemplateVersionUpsertInput = {
        templateCode,
        versionNumber: form.versionNumber.trim(),
        pageCount: num(form.pageCount),
        signatureCount: num(form.signatureCount),
        // '' clears the condition on PATCH (server: NullIfBlank); tokens are OR-ed.
        conditionExpression: form.conditions.join('|'),
        notes: form.notes.trim() || null,
        // Server overrides with the signed-in user; kept for the bypass-key path.
        authoredBy: actor,
      };
      if (!editing) {
        payload.status = 'Draft';
        const src = siblings.find((v) => String(v.versionId) === form.copyFromVersionId);
        payload.contentHtml = src?.contentHtml ?? '';
      }
      const result = editing
        ? await updateVersion(Number(initial?.versionId ?? 0), payload)
        : await createVersion(payload);
      if (!result.success) {
        const msg = result.error ?? 'Save failed.';
        setInlineErr(msg);
        onError(msg);
        return;
      }
      onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed.';
      setInlineErr(msg);
      onError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!busy) onClose();
      }}
      title={editing ? `Edit draft ${form.versionNumber}` : `New draft for ${templateCode}`}
      width={680}
      closeOnBackdrop={!busy}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" onClick={() => void submit()} disabled={!canSave}>
            {busy ? 'Saving…' : editing ? 'Save draft' : 'Create draft'}
          </button>
        </>
      }
    >
      {inlineErr && (
        <div className="banner banner--err" role="alert" style={{ marginBottom: 12 }}>
          {inlineErr}
        </div>
      )}
      <p style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 0 }}>
        A draft is never used in a live package. After you edit its text, submit it for
        approval; once approved and activated it replaces the current active version.
      </p>
      <FieldGrid columns={2}>
        <Field label="Version number" required>
          <TextInput value={form.versionNumber} onChange={(e) => set('versionNumber', e.target.value)} maxLength={16} />
        </Field>
        {!editing ? (
          <Field label="Start from" hint="Copies that version's text into the new draft.">
            <Select value={form.copyFromVersionId} onChange={(e) => set('copyFromVersionId', e.target.value)} aria-label="Start from">
              <option value="">An empty page</option>
              {siblings.map((v) => (
                <option key={v.versionId} value={String(v.versionId)}>
                  Version {String(v.versionNumber)} ({statusLabel(v.status)})
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <div />
        )}
        <Field label="Pages">
          <NumberInput value={form.pageCount} onChange={(e) => set('pageCount', e.target.value)} min={0} />
        </Field>
        <Field label="Signatures">
          <NumberInput value={form.signatureCount} onChange={(e) => set('signatureCount', e.target.value)} min={0} />
        </Field>
        <Field
          label="Only include it when"
          span={2}
          hint="Nothing selected = always included (when the template's filters match). Several = any one of them."
        >
          <ChipPicker
            items={conditionItems}
            values={form.conditions}
            onChange={(v) => set('conditions', v)}
            allLabel="Always"
            ariaLabel="Only include it when"
          />
        </Field>
        <Field label="Notes" span={2} hint="Why this version exists — shown on the version timeline.">
          <TextArea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} />
        </Field>
      </FieldGrid>
    </Modal>
  );
}
